import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { useAppbarTitle } from '@hooks/useAppbarTitle';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined';
import { Avatar, Box, Button, Chip, CircularProgress, Tooltip, Typography } from '@mui/material';
import { format, parseISO } from 'date-fns';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useFarmerSeasonalOneTimeOffersStore } from '../services';
import { getRequestDetailById } from '../services/services';
import { RequestType } from '../types';
import { ApiResponse } from '@config/api';

// You would need to import or create these components
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { Attachments } from '@features/specialistOffer/components/Attachments';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { useTruncate } from '@hooks/useTruncate';

// Mock imports (replace with actual imports as needed)
import speak from '@assets/icons/speak.svg';

const flagCDN = 'https://flagcdn.com/w160/';

export const OfferDetailPage: React.FC = () => {
	useAppbarTitle('offerDetails');

	const { t } = useTranslation();
	const { offerId } = useParams<{ offerId: string }>();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { truncate, shouldTruncate } = useTruncate();
	const modalDimensions = useModalDimensions();

	const {
		selectedOffer,
		isOfferDetailLoading,
		offerDetailError,
		setSelectedOffer,
		setIsOfferDetailLoading,
		setOfferDetailError,
	} = useFarmerSeasonalOneTimeOffersStore();

	const [mapOpen, setMapOpen] = useState(false);

	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

	// Fetch request details when component mounts
	useEffect(() => {
		const fetchOfferDetails = async () => {
			if (!offerId) return;

			try {
				setIsOfferDetailLoading(true);
				const offerDetails = await getRequestDetailById(offerId);
				console.log('Offer details:', offerDetails);
				console.log('End date:', offerDetails.end_date);
				console.log('Original date:', offerDetails.original_date);
				console.log('Payment status:', offerDetails.payment_status);
				setSelectedOffer(offerDetails);
				setIsOfferDetailLoading(false);
			} catch (error) {
				setIsOfferDetailLoading(false);
				setOfferDetailError(
					error instanceof Error ? error.message : 'Failed to fetch offer details'
				);
			}
		};

		fetchOfferDetails();

		// Cleanup function
		return () => {
			setSelectedOffer(null);
		};
	}, [offerId, setIsOfferDetailLoading, setSelectedOffer, setOfferDetailError]);

	// Pre-compute values that depend on selectedOffer, even if we might not use them
	// This ensures hooks are always called in the same order
	const displayStartDate = useMemo(() => {
		if (!selectedOffer) return '';
		const { payment_status, original_date, start_date } = selectedOffer;
		if (payment_status === 'unpaid' && original_date?.start_date) {
			return original_date.start_date;
		}
		return start_date || '';
	}, [selectedOffer]);

	const displayEndDate = useMemo(() => {
		if (!selectedOffer) return '';
		const { payment_status, original_date, end_date } = selectedOffer;
		if (payment_status === 'unpaid' && original_date?.end_date) {
			return original_date.end_date;
		}
		return end_date || '';
	}, [selectedOffer]);

	// Calculate days remaining based on end_date (not displayEndDate)
	const daysRemaining = useMemo(() => {
		if (!selectedOffer) return 3;
		
		// Always use end_date for the "Request is valid for X days" calculation
		const endDateToUse = selectedOffer.end_date;
		
		if (endDateToUse) {
			try {
				// For ISO format like "2025-07-25T13:15:08.000Z"
				const endDateObj = new Date(endDateToUse);
				
				if (endDateObj instanceof Date && !isNaN(endDateObj.getTime())) {
					const currentDate = new Date();
					console.log('End date for calculation:', endDateObj);
					console.log('Current date:', currentDate);
					
					// Calculate time difference
					const diffTime = endDateObj.getTime() - currentDate.getTime();
					console.log('Diff time (ms):', diffTime);
					
					// Convert to days
					const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					console.log('Days remaining:', diffDays);
					
					// Return positive days or 0 if expired
					return Math.max(0, diffDays);
				}
			} catch (error) {
				console.error('Error calculating days remaining:', error);
			}
		}
		
		// Fallback to request_valid_for_days or default value
		return selectedOffer.request_valid_for_days ? parseInt(selectedOffer.request_valid_for_days) : 3;
	}, [selectedOffer]);

	const handleGoBack = () => {
		navigateWithLanguage('/farmer/seasonal-onetime/offers');
	};

	const handleNavigateToChat = async (): Promise<void> => {
		if (selectedOffer?.channelId) {
			// Use existing channel ID if available
			navigateWithLanguage(`/farmer/chats?channelId=${selectedOffer.channelId}`);
		} else if (selectedOffer?.request_id) {
			// Try to get the channel ID directly
			try {
				const { chatService } = await import('services/chat/chatService');
				
				// If we have specialist information in the selectedOffer, use that
				if (selectedOffer.specialist_id) {
					const channelId = await chatService.getChannelIdByRequestAndUserId(
						selectedOffer.request_id,
						selectedOffer.specialist_id
					);
					
					if (channelId) {
						navigateWithLanguage(`/farmer/chats?channelId=${channelId}`);
						return;
					}
				}
				
				console.log('No channel ID available for this offer');
			} catch (error) {
				console.error('Error getting channel ID:', error);
			}
		} else {
			console.log('No channel ID available for this offer');
		}
	};

	const openMapsModal = () => setMapOpen(true);
	const closeMapsModal = () => setMapOpen(false);

	const isSeasonalConsultation = useMemo(() => {
		if (!selectedOffer) return false;
		return selectedOffer.request_type === RequestType.SEASONAL;
	}, [selectedOffer]);

	if (isOfferDetailLoading) {
		return (
			<Box className="flex justify-center items-center h-96">
				<CircularProgress />
			</Box>
		);
	}

	if (offerDetailError || !selectedOffer) {
		return (
			<Box className="flex flex-col items-center justify-center h-96">
				<Typography color="error" className="mb-4">
					{offerDetailError || 'Offer not found'}
				</Typography>
				<Button variant="text" onClick={handleGoBack}>
					{t('back')}
				</Button>
			</Box>
		);
	}

	const {
		crop,
		farmer,
		request_type,
		start_date,
		end_date,
		sent_date,
		farm_details,
		communication_languages,
		description,
		attachments,
		is_sent,
		price,
		duration_days,
		payment_status,
		original_date,
	} = selectedOffer;

	return (
		<div className="flex flex-col gap-[20px] pb-5 max-md:px-5">
			{/* Back Button */}
			<div className="flex justify-between items-center mb-[15px]">
				<Button
					onClick={handleGoBack}
					variant="text"
					sx={{
						color: '#343A40',
						fontFamily: 'Roboto',
						textDecoration: 'underline',
						fontSize: '16px',
						fontStyle: 'normal',
						fontWeight: 700,
						lineHeight: 'normal',
						textTransform: 'capitalize',
						gap: '10px',
						padding: 0,
						'@media (max-width: 430px)': {
							gap: '5px',
							fontSize: '14px',
						},
					}}
				>
					<ArrowBackOutlinedIcon />
					{t('back')}
				</Button>
			</div>
			{/* Top Section with Crop, Farmer, and Consultation Info */}
			<div className="flex flex-col md:flex-row items-start md:items-center gap-0 md:gap-[35px]">
				{/* Crop Information */}
				<div className="flex items-center gap-[20px] border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					<Avatar
						src={crop.image_url}
						alt={crop.name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<div className="flex flex-col gap-[10px]">
						{shouldTruncate(crop.name, 20) ? (
							<Tooltip title={crop.name}>
								<h4 className="text-dark font-roboto text-base font-bold">
									{truncate(crop.name, 20)}
								</h4>
							</Tooltip>
						) : (
							<h4 className="text-dark font-roboto text-base font-bold">{crop.name}</h4>
						)}
					</div>
				</div>

				{/* Vertical Divider */}
				<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

				{/* Farmer Info */}
				<div className="flex items-center gap-[20px] border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					<Avatar
						src={farmer.profile_image_url}
						alt={farmer.full_name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<div className="flex flex-col gap-[5px]">
						{shouldTruncate(farmer.full_name, 20) ? (
							<Tooltip title={farmer.full_name}>
								<h4 className="text-dark font-roboto text-base font-bold">
									{truncate(farmer.full_name, 20)}
								</h4>
							</Tooltip>
						) : (
							<h4 className="text-dark font-roboto text-base font-bold">{farmer.full_name}</h4>
						)}
						{farmer.country && (
							<h6 className="text-secondary font-roboto text-base font-normal flex items-center gap-[5px]">
								<Avatar
									src={`${flagCDN}${farmer?.country?.alpha_two.toLowerCase()}.png`}
									alt={farmer?.country?.alpha_two}
									style={{ width: '20px', height: '16px', borderRadius: 0 }}
								/>
								{farmer.country.name}
							</h6>
						)}
					</div>

					{/* Price Box */}
					{is_sent && price ? (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								ml: 'auto',
								width: '64px',
								height: '64px',
								backgroundColor: '#FFF9EB',
								borderRadius: '8px',
								border: '1px solid #FFDB8E',
							}}
						>
							<Typography
								variant="h4"
								sx={{
									fontWeight: 'bold',
									color: '#343a40',
								}}
							>
								€{price}
							</Typography>
						</Box>
					) : (
						<></>
					)}
				</div>

				{/* Vertical Divider */}
				<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

				<div className="flex flex-col items-center gap-2 border-b border-[#E9ECEF] md:border-none mb-[20px] md:mb-0 md:pb-0 pb-[20px] w-full md:w-auto">
					<span className="text-dark font-roboto text-base font-bold">
						{request_type === RequestType.SEASONAL
							? t('findSpecialistPage.seasonalConsultation')
							: t('ONE_TIME')}
					</span>
					{request_type === RequestType.ONE_TIME && (
						<span className="text-secondary font-roboto text-base font-normal">
							{t('oneTimeDuration', {
								duration: duration_days,
							})}
						</span>
					)}
				</div>

				{/* Vertical Divider */}
				<div className="h-[50px] border-r border-[#E9ECEF] hidden md:block"></div>

				<div className="flex items-center gap-[20px]">
					<div className="p-[10px] bg-disabled rounded-[8px]">
						<DateRangeOutlinedIcon sx={{ color: '#7B809A' }} />
					</div>
					<div className="flex flex-col">
						<h4 className="text-dark font-roboto text-base font-bold">
							{end_date
								? format(parseISO(end_date), 'dd.MM.yyyy')
								: sent_date
									? format(parseISO(sent_date), 'dd.MM.yyyy')
									: ''}
						</h4>
						<h6 className="text-secondary font-roboto text-base font-normal">
							{t('requestValidForDays', { days: daysRemaining })}
						</h6>
					</div>
				</div>
			</div>
			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>
			{isSeasonalConsultation && (
				<div className="flex flex-col border-b border-[#E9ECEF] pb-[20px] gap-[15px]">
					<h6 className="text-dark roboto-bold text-[16px] leading-normal">
						{t('consultationPeriod')}
					</h6>
					<div className="flex gap-[15px]">
						<div className="flex gap-[15px] flex-col md:flex-row">
							<div className="flex items-center gap-[15px]">
								<CalendarMonthIcon sx={{ color: '#7B809A' }} />
								<p className="text-secondary font-roboto text-sm font-normal">
									{moment(displayStartDate).format('DD.MM.YYYY')} -{' '}
									{moment(displayEndDate).format('DD.MM.YYYY')}
								</p>
							</div>
						</div>
					</div>
				</div>
			)}
			{/* Farm Details Section */}
			<div className="flex flex-col border-b border-[#E9ECEF] pb-[20px] gap-[15px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">{t('farmDetails')}</h6>
				<div className="flex gap-[15px]">
					<div className="flex gap-[15px] flex-col md:flex-row">
						<div className="flex items-center gap-[15px]">
							<MapsHomeWorkOutlinedIcon sx={{ color: '#7B809A' }} />
							<p className="text-secondary font-roboto text-sm font-normal">
								{farm_details.area} {farm_details.unit}
							</p>
						</div>
						<div className="flex items-center gap-[15px]">
							<FmdGoodOutlinedIcon sx={{ color: '#7B809A' }} />
							<div className="flex items-center">
								<p className="text-secondary font-roboto text-sm font-normal">
									{truncate(farm_details.location.address, 50)}
									<span
										className="text-primary font-roboto text-sm font-bold text-right underline decoration-solid pl-[10px] cursor-pointer"
										onClick={openMapsModal}
									>
										{t('viewOnMap')}
									</span>
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Communication Languages Section */}
			<div className="flex flex-col gap-[10px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">
					{t('communicationLanguage')}
				</h6>
				<div className="flex gap-[10px] items-center">
					<img className="text-secondary" src={speak} alt="speak" />
					<div className="flex gap-[10px] items-center">
						{communication_languages.map((language: { id: string; name: string }) => (
							<Chip
								key={language.id}
								label={language.name}
								variant="outlined"
								sx={{
									width: 'max-content',
									borderRadius: '8px',
									border: '1px solid #F8F9FA',
									backgroundColor: '#F8F9FA',
									padding: '1px 10px',
									color: '#7B809A',
									fontFamily: 'Roboto',
									fontSize: '16px',
									fontStyle: 'normal',
									fontWeight: 400,
									lineHeight: '140%',
									height: '24px',
									textTransform: 'camelcase',
									'& .MuiChip-label': {
										padding: 0,
									},
								}}
							/>
						))}
					</div>
				</div>
			</div>
			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>
			{/* Description Section */}
			<div className="flex flex-col gap-[10px]">
				<h6 className="text-dark roboto-bold text-[16px] leading-normal">{t('info')}</h6>
				<div
					className="text-secondary roboto-regular text-[16px] leading-normal flex flex-col gap-[10px] w-full"
					dangerouslySetInnerHTML={{ __html: description || '' }}
				></div>
			</div>
			{/* Horizontal Divider */}
			<div className="w-full h-[1px] bg-light"></div>
			{/* Attachments Section */}
			{attachments && attachments.length > 0 && <Attachments files={attachments} />}
			{/* Maps Modal */}
			<GeneralMuiModal
				maxWidth="xl"
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={closeMapsModal}
				content={
					<TaMaps
						disabled={true}
						marker={{
							lng: Number(farm_details.location.longitude),
							lat: Number(farm_details.location.latitude),
						}}
						currentAddress={farm_details.location.address}
					/>
				}
			/>
		</div>
	);
};
